import React, { useState, useEffect, useCallback } from "react";
import { Button, Table, Modal, Form, Input, message, Typography, Card, Row, Popconfirm, Dropdown, Menu, Drawer } from "antd";
import ToolService from "../../services/ToolService";
import { openNotification } from "../../utils";
import { SearchOutlined } from '@ant-design/icons';
import QuestionList from "../../modules/questions/views/List";
import ToolPreparationDrawer, { TOOL_PREPARATION_UPDATED_EVENT } from "./ToolPreparationDrawer";

interface ToolListProps {
    questionToolId?: number | null;
    onQuestionDrawerChange?: (visible: boolean, toolId?: number) => void;
}

const ToolList: React.FC<ToolListProps> = ({ questionToolId, onQuestionDrawerChange }) => {
    const [tools, setTools] = useState<any[]>([]);
    const [filteredTools, setFilteredTools] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingTool, setEditingTool] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
    const [selectedToolForQuestions, setSelectedToolForQuestions] = useState<any | null>(null);
    const [preparationDrawerToolId, setPreparationDrawerToolId] = useState<number | null>(null);
    const [preparationDrawerKey, setPreparationDrawerKey] = useState(0);
    const [form] = Form.useForm();
    const { Title } = Typography;

    const sortToolsByName = useCallback((items: any[] = []) => {
        return [...items].sort((a, b) =>
            (a?.name ?? '').localeCompare(b?.name ?? '', 'tr-TR', { sensitivity: 'base' })
        );
    }, []);

    const normalizeText = (value?: string) => (value ?? '').toLocaleLowerCase('tr-TR');

    useEffect(() => {
        const normalizedSearch = normalizeText(searchTerm);
        const filtered = tools.filter((tool) => {
            const matchesSearch =
                normalizeText(tool.name).includes(normalizedSearch) ||
                normalizeText(tool.description).includes(normalizedSearch);

            return matchesSearch;
        });

        setFilteredTools(sortToolsByName(filtered));
    }, [searchTerm, tools, sortToolsByName]);

    const loadTools = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ToolService.getAllTools();
            const sorted = sortToolsByName(response.data);
            setTools(sorted);
            setFilteredTools(sorted);
        } catch (error) {
            message.error("Error loading tools.");
        } finally {
            setLoading(false);
        }
    }, [sortToolsByName]);

    useEffect(() => {
        loadTools();
    }, [loadTools]);

    useEffect(() => {
        const handlePreparationUpdated = () => {
            loadTools();
        };
        window.addEventListener(TOOL_PREPARATION_UPDATED_EVENT, handlePreparationUpdated);
        return () => {
            window.removeEventListener(TOOL_PREPARATION_UPDATED_EVENT, handlePreparationUpdated);
        };
    }, [loadTools]);

    useEffect(() => {
        if (questionToolId === undefined) {
            if (typeof onQuestionDrawerChange === 'function' && isQuestionDrawerOpen) {
                setIsQuestionDrawerOpen(false);
                setSelectedToolForQuestions(null);
            }
            return;
        }
        if (questionToolId === null) {
            if (isQuestionDrawerOpen) {
                setIsQuestionDrawerOpen(false);
                setSelectedToolForQuestions(null);
            }
            return;
        }
        const numericId = Number(questionToolId);
        if (!Number.isFinite(numericId)) {
            return;
        }
        const match = tools.find((tool) => Number(tool?.id) === numericId);
        if (match) {
            setSelectedToolForQuestions(match);
            setIsQuestionDrawerOpen(true);
        }
    }, [questionToolId, tools, isQuestionDrawerOpen]);

    useEffect(() => {
        if (!isQuestionDrawerOpen || !selectedToolForQuestions) {
            return;
        }
        const match = tools.find((tool) => Number(tool?.id) === Number(selectedToolForQuestions.id));
        if (match && match !== selectedToolForQuestions) {
            setSelectedToolForQuestions(match);
        }
    }, [tools, isQuestionDrawerOpen, selectedToolForQuestions]);


    const handleCreate = () => {
        setEditingTool(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (tool: any) => {
        setEditingTool(tool);
        form.setFieldsValue(tool);
        setIsModalVisible(true);
    };

    const handleOpenQuestions = (tool: any) => {
        if (!tool) {
            return;
        }
        setSelectedToolForQuestions(tool);
        setIsQuestionDrawerOpen(true);
        onQuestionDrawerChange?.(true, tool?.id);
    };

    const handleOpenPreparationDrawer = (tool: any) => {
        if (!tool || !tool.id) {
            return;
        }
        setPreparationDrawerToolId(tool.id);
        setPreparationDrawerKey((prev) => prev + 1);
    };

    const handleCloseQuestions = () => {
        setIsQuestionDrawerOpen(false);
        setSelectedToolForQuestions(null);
        onQuestionDrawerChange?.(false);
    };

    const handleDelete = async (id: number) => {
        try {
            await ToolService.deleteTool(id);
            openNotification('success', 'Başarılı', "Bileşen silindi.");
            loadTools();
        } catch (error: any) {
            if (error.response && error.response.status === 409) {
                openNotification('error', 'Hata', "Silme işlemi gerçekleştirilemez. Bu bileşen aktif olarak kullanılmaktadır.");
            } else {
                openNotification('error', 'Hata', "Bileşen silinemedi.");
            }
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            const payload = editingTool ? { ...editingTool, ...values } : values;
            if (editingTool) {
                await ToolService.updateTool(editingTool.id, {
                    name: payload.name,
                    description: payload.description,
                    preparationText: payload.preparationText ?? null,
                    preparationTitle: payload.preparationTitle ?? null,
                    preparationDocumentId: payload.preparationDocumentId ?? null,
                });
                openNotification('success', 'Başarılı', "Bileşen güncellendi.");
            } else {
                await ToolService.createTool({
                    name: payload.name,
                    description: payload.description,
                });
                openNotification('success', 'Başarılı', "Bileşen oluşturuldu.");
            }
            loadTools();
            setIsModalVisible(false);
        } catch (error) {
            openNotification('error', 'Başarısız', "İşlemde hata meydana geldi.");
        }
    };

    const menu = (record: any) => (
        <Menu>
            <Menu.Item key={`edit-${record.id}`} onClick={() => handleEdit(record)}>
                Düzenle
            </Menu.Item>
            <Menu.Item key={`preparation-${record.id}`} onClick={() => handleOpenPreparationDrawer(record)}>
                Ön Hazırlık Metni
            </Menu.Item>
            <Menu.Item key={`questions-${record.id}`} onClick={() => handleOpenQuestions(record)}>
                Sorular
            </Menu.Item>
            <Menu.Item key={`delete-${record.id}`}>
                <Popconfirm placement="left"
                    title="Bu bileşeni silmek istediğinize emin misiniz?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Evet"
                    cancelText="Hayır"
                >
                    <a>Sil</a>
                </Popconfirm>
            </Menu.Item>
        </Menu>
    );

    const columns = [
        {
            title: "Adı",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Açıklama",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "İşlemler",
            key: "actions",
            width: 220,
            render: (_: any, record: any) => (
                <Dropdown overlay={menu(record)} trigger={['click']}>
                    <Button className="action-button width-auto" type="default">
                        İşlemler
                    </Button>
                </Dropdown>
            ),
        },
    ];

    return (
        <Card className="br-30px">
            <Row justify="space-between" align={'middle'} style={{ marginBottom: 16 }}>
                <div>
                    <Title style={{ margin: 0, display: 'inline-block' }} level={4}>Bileşen Listesi</Title>
                    <Typography.Text type='secondary' className='ms-5'>
                        <strong>({filteredTools.length})</strong> kayıt listelendi
                    </Typography.Text>
                </div>
                <Row>
                    <Input
                                      allowClear
                        placeholder="Ara"
                        prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                        className="d-input d-input-form mr-16px d-input-filter"
                        style={{ width: 200, marginRight: 16 }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button type="primary" className="d-button dark-button width-auto" onClick={handleCreate}>
                        Yeni Bileşen Ekle
                    </Button>
                </Row>
            </Row>
            <Table
                dataSource={filteredTools}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '30'],
                }}
            />
            <Modal
                title={editingTool ? "Bileşen Düzenle" : "Yeni Bileşen Ekle"}
                visible={isModalVisible}
                footer={null}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Form.Item
                        label="Adı"
                        name="name"
                        rules={[{ required: true, message: 'Lütfen bileşen adı giriniz' },
                        { max: 255, message: 'Adı en fazla 255 karakter olabilir' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Açıklama"
                        name="description"
                        rules={[
                            { max: 1000, message: 'Açıklama en fazla 1000 karakter olabilir' },
                        ]}
                    >
                        <Input.TextArea />
                    </Form.Item>

                    <Row justify={'center'}>
                        <Button type="primary" htmlType="submit" className="d-button dark-button width-auto">
                            {editingTool ? "Güncelle" : "Kaydet"}
                        </Button>
                    </Row>
                </Form>
            </Modal>
            <Drawer
                open={isQuestionDrawerOpen}
                onClose={handleCloseQuestions}
                destroyOnClose
                width={960}
                title={selectedToolForQuestions ? `${selectedToolForQuestions?.name ?? 'Bileşen'} Soruları` : 'Sorular'}
            >
                {selectedToolForQuestions ? (
                    <QuestionList key={selectedToolForQuestions.id} toolId={selectedToolForQuestions.id} />
                ) : null}
            </Drawer>
            <ToolPreparationDrawer
                key={preparationDrawerKey}
                toolId={preparationDrawerToolId}
            />
        </Card>
    );
};

export default ToolList;
